import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Shield, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/lib/auth";

export function AdminPasswords() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [target, setTarget] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, p, c, m] = await Promise.all([
      api.get("/admin/users",{params:{role:"associate"}}),
      api.get("/admin/users",{params:{role:"professional"}}),
      api.get("/admin/users",{params:{role:"clinic"}}),
      api.get("/admin/users",{params:{role:"master"}}),
    ]);
    setUsers([...m.data, ...a.data, ...p.data, ...c.data]);
  };
  useEffect(()=>{ load(); }, []);

  const demoEmails = ["j.girodabaixada@gmail.com","demo@giromaisaude.com.br","profissional@giromaisaude.com.br","clinica@giromaisaude.com.br"];
  const filtered = users.filter(u => (!filter || u.email.includes(filter.toLowerCase()) || u.name?.toLowerCase().includes(filter.toLowerCase())));

  const submit = async () => {
    if (pw.length < 8) return toast.error("A senha precisa ter ao menos 8 caracteres");
    if (pw !== pw2) return toast.error("As senhas não coincidem");
    setSaving(true);
    try {
      await api.patch(`/admin/users/${target.id}/password`, {new_password: pw});
      toast.success(`Senha de ${target.email} atualizada`);
      setTarget(null); setPw(""); setPw2(""); setShow(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const ROLE_LABEL = {master:"Master", admin:"Admin", associate:"Associado", professional:"Profissional", clinic:"Clínica"};
  const ROLE_COLOR = {
    master:"bg-[#0F172A] text-white",
    admin:"bg-[#0F172A] text-white",
    associate:"bg-[#E6F4F1] text-[#0D7C66]",
    professional:"bg-[#FEF8F3] text-[#FF7D54]",
    clinic:"bg-slate-100 text-slate-700",
  };

  return (
    <div className="space-y-4" data-testid="admin-passwords">
      <Card className="p-5 gs-shadow border-l-4 border-l-[#0D7C66]">
        <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#0D7C66]"/><div className="font-bold">Trocar senhas antes de publicar</div></div>
        <p className="text-sm text-slate-600 mt-1">Antes de apontar o domínio www.giromaisaude.com.br, troque as senhas dos 4 usuários demo. Depois disso, as novas senhas serão preservadas mesmo após reiniciar o servidor.</p>
      </Card>

      <Card className="p-5 gs-shadow">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="font-bold flex items-center gap-2"><KeyRound className="w-5 h-5"/>Gerenciar senhas</div>
          <Input data-testid="pwd-filter" value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Buscar por nome ou e-mail" className="max-w-xs"/>
        </div>
        <div className="space-y-2">
          {filtered.map(u => (
            <div key={u.id} className="flex items-center justify-between gap-3 border-b border-slate-100 py-3" data-testid={`pwd-row-${u.email}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <b className="truncate">{u.name}</b>
                  <Badge className={`${ROLE_COLOR[u.role] || "bg-slate-100"} border-0`}>{ROLE_LABEL[u.role] || u.role}</Badge>
                  {demoEmails.includes(u.email) && <Badge className="bg-amber-100 text-amber-800 border-0">Demo</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{u.email}</div>
              </div>
              <Button size="sm" variant="outline" data-testid={`pwd-change-${u.email}`} onClick={()=>{setTarget(u); setPw(""); setPw2("");}} disabled={u.role==="master" && user?.role!=="master"}>
                <KeyRound className="w-4 h-4 mr-1"/>Trocar senha
              </Button>
            </div>
          ))}
          {filtered.length===0 && <div className="text-sm text-slate-500 text-center py-6">Nenhum usuário encontrado.</div>}
        </div>
      </Card>

      <Dialog open={!!target} onOpenChange={(v)=>{if(!v){setTarget(null); setPw(""); setPw2(""); setShow(false);}}}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nova senha para {target?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600 break-all">E-mail: <b>{target?.email}</b></div>
            <div className="relative">
              <Input data-testid="pwd-new" type={show?"text":"password"} placeholder="Nova senha (mín. 8 caracteres)" value={pw} onChange={e=>setPw(e.target.value)}/>
              <button type="button" aria-label="Mostrar senha" onClick={()=>setShow(s=>!s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-900">{show?<EyeOff className="w-4 h-4"/>:<Eye className="w-4 h-4"/>}</button>
            </div>
            <Input data-testid="pwd-new-confirm" type={show?"text":"password"} placeholder="Confirmar nova senha" value={pw2} onChange={e=>setPw2(e.target.value)}/>
            <div className="text-[11px] text-slate-500">Use pelo menos 8 caracteres. Recomendado: letras maiúsculas, números e símbolos.</div>
          </div>
          <DialogFooter>
            <Button data-testid="pwd-submit" onClick={submit} disabled={saving || !pw || !pw2} className="bg-[#0D7C66] hover:bg-[#074E41]">{saving?"Salvando...":"Salvar nova senha"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
